/**
 * Turning a router URL into something safe to report.
 *
 * Two jobs, and both matter as much as each other:
 *  - **Privacy.** The query string is where this app keeps free-text patient
 *    filters, and the fragment is where an SSO landing keeps its access token.
 *    Neither may reach Google.
 *  - **Readable reports.** `/tests/665f…` and `/tests/667a…` are the same page
 *    to everyone reading a report, so record ids collapse to `:id` instead of
 *    spreading one visit across thousands of rows.
 */
import { MAX_NAME_LENGTH } from './analytics.constants';

/** Segments replaced with `:id`, i.e. everything that identifies one record. */
const ID_LIKE_SEGMENT: readonly RegExp[] = [
  /^[0-9a-f]{24}$/i, // Mongo ObjectId
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // UUID
  /^\d+$/, // numeric id
  /^[A-Za-z0-9_-]{20,}$/, // opaque token; longer than any route segment we own
];

const MASK = ':id';

function maskSegment(segment: string): string {
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // Malformed escape sequence; judge the raw segment instead.
  }

  if (decoded.includes('@') || decoded.length > MAX_NAME_LENGTH) return MASK;
  return ID_LIKE_SEGMENT.some((pattern) => pattern.test(decoded)) ? MASK : decoded;
}

function keepAllowedQuery(
  rawQuery: string | undefined,
  allowedQueryParams: readonly string[],
): string {
  if (!rawQuery || allowedQueryParams.length === 0) return '';

  return rawQuery
    .split('&')
    .filter((pair) => allowedQueryParams.includes(pair.split('=')[0]))
    .join('&');
}

/**
 * Strip a URL down to path only, with id-like segments masked and the query
 * dropped except for explicitly allowed keys.
 *
 * Pure by design — the allowlist is passed in rather than read from module
 * state, so this is safe to reuse anywhere a URL is about to be logged.
 */
export function sanitizeAnalyticsPath(
  url: string,
  allowedQueryParams: readonly string[] = [],
): string {
  if (!url) return '/';

  const [beforeHash] = url.split('#');
  const [rawPath, rawQuery] = beforeHash.split('?');

  const path = '/' + rawPath.split('/').filter(Boolean).map(maskSegment).join('/');
  const query = keepAllowedQuery(rawQuery, allowedQueryParams);

  return query ? `${path}?${query}` : path;
}
